import { Component, Input, Output, EventEmitter, ChangeDetectionStrategy, computed, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { TranslateModule } from '@ngx-translate/core';
import moment from 'moment';
import 'moment/locale/fr';
import { LoadingSpinnerFuseComponent } from '../../loading-spinner-fuse/loading-spinner-fuse.component';

export interface AvailableDay {
  day: string; // ISO date
  dispo: any[];
}

@Component({
  selector: 'app-ponctual-calendar-selector-fuse',
  standalone: true,
  imports: [
    CommonModule,
    MatIconModule,
    MatButtonModule,
    MatProgressSpinnerModule,
    TranslateModule,
    LoadingSpinnerFuseComponent,
  ],
  templateUrl: './ponctual-calendar-selector-fuse.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PonctualCalendarSelectorFuseComponent {
  @Input() availableDays: AvailableDay[] = [];
  @Input() selectedDate: Date | null = null;
  @Input() loading: boolean = false;
  @Input() year: number = moment().year();
  @Input() month: number = moment().month() + 1; // 1-12

  @Output() dateSelected = new EventEmitter<Date>();
  @Output() monthChanged = new EventEmitter<{ year: number; month: number }>();

  get monthName(): string {
    moment.locale('fr');
    return moment().month(this.month - 1).format('MMMM');
  }

  get capitalizedMonthName(): string {
    return this.monthName.charAt(0).toUpperCase() + this.monthName.slice(1);
  }

  get calendarDays(): (number | null)[] {
    const firstDay = new Date(this.year, this.month - 1, 1).getDay();
    const daysInMonth = new Date(this.year, this.month, 0).getDate();
    const offset = firstDay === 0 ? 6 : firstDay - 1; // Lundi = 0

    const days: (number | null)[] = [];
    for (let i = 0; i < offset; i++) {
      days.push(null);
    }
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(i);
    }
    return days;
  }

  isDayAvailable(day: number | null): boolean {
    if (day === null) return false;
    const dateISO = `${this.year}-${this.month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`;
    return this.availableDays.some(avail => moment(avail.day).format('YYYY-MM-DD') === dateISO);
  }

  isDaySelected(day: number | null): boolean {
    if (day === null || !this.selectedDate) return false;
    const dateISO = `${this.year}-${this.month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`;
    const selectedISO = moment(this.selectedDate).format('YYYY-MM-DD');
    return dateISO === selectedISO;
  }

  selectDay(day: number | null): void {
    if (day !== null && this.isDayAvailable(day)) {
      const date = new Date(this.year, this.month - 1, day);
      this.dateSelected.emit(date);
    }
  }

  previousMonth(): void {
    let newMonth = this.month - 1;
    let newYear = this.year;

    if (newMonth < 1) {
      newMonth = 12;
      newYear--;
    }

    this.monthChanged.emit({ year: newYear, month: newMonth });
  }

  nextMonth(): void {
    let newMonth = this.month + 1;
    let newYear = this.year;

    if (newMonth > 12) {
      newMonth = 1;
      newYear++;
    }

    this.monthChanged.emit({ year: newYear, month: newMonth });
  }
}
